/**
 * F28 (Wave 2C-ii) — admin email-suppressions API client (real surface).
 *
 * Endpoints:
 *   GET    /admin/email-suppressions
 *     Perm: `email_suppressions:read` (super_admin + support + content_moderator)
 *     Query: { source?, q?, cursor?, limit? }
 *   POST   /admin/email-suppressions
 *     Perm: `email_suppressions:write` (super_admin + support only)
 *     Body: AddEmailSuppressionInput (email + reason + optional notes)
 *   DELETE /admin/email-suppressions/:emailAddress
 *     Perm: `email_suppressions:write`
 *     Body: RemoveEmailSuppressionInput (mandatory reason >= 10 chars)
 *
 * RBAC is enforced server-side. The page-level <ProtectedRoute> gates
 * navigation; the API client forwards requests and surfaces the server's
 * 403 to the caller.
 *
 * For tests, the admin vitest config aliases this module to
 * `./emailSuppressions.fixture` so component tests drive an in-memory mock.
 */
import type {
  AddEmailSuppressionInput,
  AdminPageMeta,
  EmailSuppression,
  ListEmailSuppressionsQuery,
  RemoveEmailSuppressionInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

/**
 * List-row shape: the canonical EmailSuppression plus the server-resolved
 * admin display name for `addedByAdminId` (NULL for system/webhook-written
 * rows or admins without a display name).
 */
export type EmailSuppressionListRow = EmailSuppression & {
  readonly addedByAdminDisplayName: string | null;
};

/** List response shape returned by the API (page-numbered + back-compat cursor). */
export interface ListEmailSuppressionsResult {
  readonly data: readonly EmailSuppressionListRow[];
  readonly nextCursor: string | null;
  /** Number of rows in `data` (the wire field is `totalReturned`, NOT `total` — the real filtered count lives on `pagination.total`). */
  readonly totalReturned: number;
  /** Present when `page` is supplied (page-numbered pagination). */
  readonly pagination?: AdminPageMeta;
}

/** POST result: the row plus whether it was newly created (false = the address was already suppressed and its reason/source/notes were refreshed — upsert semantics). */
export interface AddEmailSuppressionResult {
  readonly suppression: EmailSuppression;
  readonly created: boolean;
}

interface DataEnvelope<T> {
  readonly data: T;
}

function buildListQuery(filter: ListEmailSuppressionsQuery): string {
  return buildQuery({
    source: filter.source,
    q: filter.q,
    cursor: filter.cursor,
    limit: filter.limit,
    page: filter.page,
    perPage: filter.perPage,
  });
}

/** GET /admin/email-suppressions — paginated list with filters. */
export async function listEmailSuppressions(
  filter: ListEmailSuppressionsQuery,
): Promise<ListEmailSuppressionsResult> {
  return adminFetch<ListEmailSuppressionsResult>(
    `/admin/email-suppressions${buildListQuery(filter)}`,
  );
}

/**
 * POST /admin/email-suppressions — manual admin add. The server UPSERTs on
 * duplicate (re-suppressing refreshes reason/source/notes) and reports
 * `created: false` so the caller can toast an honest "already suppressed —
 * updated" variant.
 */
export async function addEmailSuppression(
  input: AddEmailSuppressionInput,
): Promise<AddEmailSuppressionResult> {
  const body = await adminFetch<
    (DataEnvelope<EmailSuppression> & { readonly created?: boolean }) | EmailSuppression
  >(`/admin/email-suppressions`, {
    method: 'POST',
    body: input as unknown as Record<string, unknown>,
  });
  // Some endpoints wrap responses in { data: ... }; tolerate both shapes.
  if (body && typeof body === 'object' && 'data' in body) {
    const envelope = body as DataEnvelope<EmailSuppression> & { readonly created?: boolean };
    return { suppression: envelope.data, created: envelope.created ?? true };
  }
  return { suppression: body as EmailSuppression, created: true };
}

/**
 * DELETE /admin/email-suppressions/:emailAddress — manual admin remove.
 * Mandatory `reason` >= 10 chars (enforced server-side via
 * RemoveEmailSuppressionInputSchema).
 */
export async function removeEmailSuppression(
  emailAddress: string,
  input: RemoveEmailSuppressionInput,
): Promise<{ readonly removed: boolean }> {
  return adminFetch<{ readonly removed: boolean }>(
    `/admin/email-suppressions/${encodeURIComponent(emailAddress)}`,
    {
      method: 'DELETE',
      body: input as unknown as Record<string, unknown>,
    },
  );
}
